import { useState, useCallback } from 'react';
import { apiCall } from 'core/api/apiService';

const useOciUsers = (user, showError, showSuccess, t) => {
  const [ociUsers, setOciUsers] = useState([]);
  const [ociLoading, setOciLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);

  const loadOciUsers = useCallback(async () => {
    try {
      setOciLoading(true);
      const users = await apiCall('/oci-users', 'GET', null, user);
      setOciUsers(users);
    } catch (error) {
      console.error('Error loading OCI users:', error);
      showError(t('admin.ociHook.loadError', { error: error.message }));
    } finally {
      setOciLoading(false);
    }
  }, [user, showError]);

  const loadOciStatistics = useCallback(async () => {
    try {
      const stats = await apiCall('/oci-users/stats', 'GET', null, user);
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading OCI statistics:', error);
    }
  }, [user]);

  const createOciUser = async (userData) => {
    try {
      if (!userData.username || !userData.password) {
        throw new Error(t('admin.ociHook.credentialsRequired'));
      }

      await apiCall('/oci-users', 'POST', userData, user);
      showSuccess(t('admin.ociHook.created'));

      loadOciUsers();
      loadOciStatistics();
    } catch (error) {
      console.error('Error creating OCI user:', error);
      throw error; // Re-throw to be handled by the component
    }
  };

  const updateOciUser = async (userId, userData) => {
    try {
      await apiCall(`/oci-users/${userId}`, 'PUT', userData, user);
      showSuccess(t('admin.ociHook.updated'));

      loadOciUsers();
      loadOciStatistics();
    } catch (error) {
      console.error('Error updating OCI user:', error);
      throw error; // Re-throw to be handled by the component
    }
  };

  const deleteOciUser = async (userId) => {
    try {
      const response = await fetch(`/api/oci-users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('admin.ociHook.deleteError'));
      }

      showSuccess(t('admin.ociHook.deleted'));
      loadOciUsers();
      loadOciStatistics();
    } catch (error) {
      console.error('Error deleting OCI user:', error);
      throw error; // Re-throw to be handled by the component
    }
  };

  const reload = () => {
    loadOciUsers();
    loadOciStatistics();
  };

  return {
    ociUsers,
    ociLoading,
    statistics,
    loadOciUsers,
    loadOciStatistics,
    createOciUser,
    updateOciUser,
    deleteOciUser,
    reload
  };
};

export default useOciUsers;